import { useState, useCallback, useMemo } from 'react'
import { WhatsAppIcon } from './WhatsAppIcon'
import { ShareIcon } from './ShareIcon'
import { WHATSAPP_NUMBER, IMAGE_QUALITY } from '../constants'
import { useAppStore } from '@/store/appStore'

interface ShareButtonsGroupProps {
  circleX: number
  circleY: number
  circleRadius: number
  buttonX: number
  buttonY: number
  shopId: string
  frameIndex: number
  whatsappNumber?: string // Shop-specific WhatsApp number (falls back to WHATSAPP_NUMBER)
}

export function ShareButtonsGroup({
  circleX,
  circleY,
  circleRadius,
  buttonX,
  buttonY,
  shopId,
  frameIndex,
  whatsappNumber,
}: ShareButtonsGroupProps) {
  const [isSharing, setIsSharing] = useState(false)
  const currentSectionId = useAppStore((state) => state.currentSectionId)

  // Detect mobile using multiple checks
  const isMobile = useMemo(() => {
    // Check 1: User agent
    const userAgent = navigator.userAgent.toLowerCase()
    const isMobileUA = /android|webos|iphone|ipad|ipod|blackberry|iemobile|opera mini/i.test(userAgent)
    
    // Check 2: Touch support
    const hasTouch = 'ontouchstart' in window || navigator.maxTouchPoints > 0
    
    // Check 3: Screen width (optional but helpful)
    const isMobileWidth = window.innerWidth <= 768
    
    return isMobileUA || (hasTouch && isMobileWidth)
  }, [])

  // Use shop-specific number or fallback to default
  const targetWhatsAppNumber = whatsappNumber || WHATSAPP_NUMBER

  // Generate shareable URL
  const shareUrl = useMemo(() => {
    const baseUrl = window.location.origin + window.location.pathname
    return `${baseUrl}?shopid=${shopId}&section=${currentSectionId}&frame=${frameIndex}&x=${circleX}&y=${circleY}&r=${circleRadius}`
  }, [shopId, currentSectionId, frameIndex, circleX, circleY, circleRadius])

  const handleWhatsApp = useCallback((e: React.MouseEvent) => {
    e.preventDefault()
    e.stopPropagation()

    setIsSharing(true)

    try {
      const fullMessage = `Hello! I found an item I'm interested in. Check it out:\n\n${shareUrl}`
      const message = encodeURIComponent(fullMessage)
      window.open(`https://wa.me/${targetWhatsAppNumber}?text=${message}`, '_blank')
    } catch (error) {
      console.error('WhatsApp share failed:', error)
      alert('Failed to open WhatsApp. Please try again.')
    } finally {
      setIsSharing(false)
    }
  }, [shareUrl, targetWhatsAppNumber])

  const handleShare = useCallback(async (e: React.MouseEvent) => {
    e.preventDefault()
    e.stopPropagation()

    setIsSharing(true)

    try {
      // Find the selected frame image - SYNCHRONOUSLY
      const frameContainer = document.querySelector('[data-selected-frame="true"]')
      if (!frameContainer) {
        throw new Error('Selected frame not found')
      }
      
      const imgElement = frameContainer.querySelector('img') as HTMLImageElement
      if (!imgElement || !imgElement.complete || !imgElement.naturalWidth) {
        throw new Error('Frame image not loaded')
      }

      // Create canvas and draw everything synchronously
      const imageSpaceWidth = 450
      const imageSpaceHeight = 800
      const canvas = document.createElement('canvas')
      canvas.width = imgElement.naturalWidth
      canvas.height = imgElement.naturalHeight
      const ctx = canvas.getContext('2d')!

      // Draw frame
      ctx.drawImage(imgElement, 0, 0)

      // Scale coordinates from image space to natural dimensions
      const scaleX = imgElement.naturalWidth / imageSpaceWidth
      const scaleY = imgElement.naturalHeight / imageSpaceHeight
      const scaledX = circleX * scaleX
      const scaledY = circleY * scaleY
      const scaledRadius = circleRadius * Math.min(scaleX, scaleY)

      // Draw blue circle
      ctx.strokeStyle = '#3b82f6'
      ctx.lineWidth = 4 * Math.min(scaleX, scaleY)
      ctx.beginPath()
      ctx.arc(scaledX, scaledY, scaledRadius, 0, Math.PI * 2)
      ctx.stroke()

      // Draw crosshair
      ctx.lineWidth = 3 * Math.min(scaleX, scaleY)
      const crosshairSize = 15 * Math.min(scaleX, scaleY)
      ctx.beginPath()
      ctx.moveTo(scaledX - crosshairSize, scaledY)
      ctx.lineTo(scaledX + crosshairSize, scaledY)
      ctx.moveTo(scaledX, scaledY - crosshairSize)
      ctx.lineTo(scaledX, scaledY + crosshairSize)
      ctx.stroke()

      // Convert to blob
      const blob = await new Promise<Blob>((resolve, reject) => {
        canvas.toBlob(
          (b) => (b ? resolve(b) : reject(new Error('Canvas to blob failed'))),
          'image/jpeg',
          IMAGE_QUALITY
        )
      })

      const file = new File([blob], 'dukkan_item.jpg', { type: 'image/jpeg' })
      const shareTitle = 'Check out this item on Dukkan'
      const shareText = `Hello! I found an item I'm interested in. Check it out:\n\n${shareUrl}`

      // Try to share with image first
      if (navigator.canShare && navigator.canShare({ files: [file] })) {
        await navigator.share({
          title: shareTitle,
          text: shareText,
          files: [file],
        })
      } else {
        // Fallback: share text only
        await navigator.share({
          title: shareTitle,
          text: shareText,
        })
      }
    } catch (error: any) {
      // User cancelled share
      if (error.name === 'AbortError') {
        return
      }
      console.error('Share failed:', error)
      // Fallback to WhatsApp
      try {
        const fullMessage = `Hello! I found an item I'm interested in. Check it out:\n\n${shareUrl}`
        const message = encodeURIComponent(fullMessage)
        window.open(`https://wa.me/${targetWhatsAppNumber}?text=${message}`, '_blank')
      } catch (fallbackError) {
        console.error('WhatsApp fallback failed:', fallbackError)
      }
    } finally {
      setIsSharing(false)
    }
  }, [circleX, circleY, circleRadius, shareUrl])

  // Calculate button positions for horizontal layout
  // WhatsApp on left, Share on right
  const whatsappButtonX = buttonX - (isMobile ? 25 : 0) // WhatsApp to the left on mobile
  const shareButtonX = buttonX + 25 // Share button to the right

  return (
    <>
      {/* WhatsApp button - always shown, positioned on the left on mobile */}
      <div
        onClick={handleWhatsApp}
        style={{
          position: 'absolute',
          left: `${whatsappButtonX}px`,
          top: `${buttonY}px`,
          transform: 'translate(-50%, -50%)',
          backgroundColor: 'rgba(0, 0, 0, 0.3)',
          backdropFilter: 'blur(10px)',
          borderRadius: '50%',
          border: '2px solid rgba(255, 255, 255, 0.5)',
          boxShadow: '0 0 12px rgba(37, 211, 102, 0.5)',
          pointerEvents: 'auto',
          cursor: isSharing ? 'wait' : 'pointer',
          zIndex: 10000,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          width: '40px',
          height: '40px',
          opacity: isSharing ? 0.5 : 1,
          transition: 'opacity 0.2s',
        }}
        onMouseEnter={(e) => {
          if (!isSharing) {
            e.currentTarget.style.backgroundColor = 'rgba(37, 211, 102, 0.3)'
            e.currentTarget.style.borderColor = 'rgba(37, 211, 102, 0.7)'
          }
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.backgroundColor = 'rgba(0, 0, 0, 0.3)'
          e.currentTarget.style.borderColor = 'rgba(255, 255, 255, 0.5)'
        }}
      >
        <WhatsAppIcon size={24} />
      </div>

      {/* Share button - mobile only, positioned on the right */}
      {isMobile && (
        <div
          onClick={handleShare}
          style={{
            position: 'absolute',
            left: `${shareButtonX}px`,
            top: `${buttonY}px`,
            transform: 'translate(-50%, -50%)',
            backgroundColor: 'rgba(0, 0, 0, 0.3)',
            backdropFilter: 'blur(10px)',
            borderRadius: '50%',
            border: '2px solid rgba(255, 255, 255, 0.5)',
            boxShadow: '0 0 12px rgba(59, 130, 246, 0.5)',
            pointerEvents: 'auto',
            cursor: isSharing ? 'wait' : 'pointer',
            zIndex: 10000,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: '40px',
            height: '40px',
            opacity: isSharing ? 0.5 : 1,
            transition: 'opacity 0.2s',
          }}
          onMouseEnter={(e) => {
            if (!isSharing) {
              e.currentTarget.style.backgroundColor = 'rgba(59, 130, 246, 0.3)'
              e.currentTarget.style.borderColor = 'rgba(59, 130, 246, 0.7)'
            }
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.backgroundColor = 'rgba(0, 0, 0, 0.3)'
            e.currentTarget.style.borderColor = 'rgba(255, 255, 255, 0.5)'
          }}
        >
          <ShareIcon size={24} />
        </div>
      )}
    </>
  )
}
